import {
  IProductListFilterItem,
  ProductList,
  ProductListFilterItemRangeValue,
  ProductListFilterItemValueType,
} from "../../../typescript-library";
import {isSearchPage, useProductListContext} from "../productListProvider/ProductListProvider";
import Drawer from "../../lib/form/Drawer/Drawer";
import ProductSortInput from "./ProductSortInput";
import LoadingSpinner from "../../atoms/LoadingSpinner";
import SelectedFilterOverview from "./SelectedFilterOverview";
import ListItems from "../productList/ListItems";
import React, {forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {filterItemArray} from "../productListProvider/helpers/filterItemArray";
import SearchHeadline from "./SearchHeadline";
import {ProductListFilterMobileType, RefType} from "./ProductListFilter";

const ProductListFilterMobile: React.ForwardRefRenderFunction<RefType, ProductListFilterMobileType> = (
  {filterNumbers, toggleFilter},
  ref,
) => {
  const {state, dispatch} = useProductListContext();
  const {t} = useTranslation();
  // Chosen filters before selecting on 'Apply Filters'
  const [mobileFilters, setMobileFilters] = useState<{[k: string]: ProductListFilterItemValueType}>({});
  const [isDrawerOpen, setDrawerOpen] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const filterHeadRef = useRef<HTMLDivElement>(null);
  const productListRef = useRef<HTMLDivElement>(null);

  useImperativeHandle(ref, () => ({
    filterHeadRef,
    productListRef,
  }));

  useEffect(() => {
    setMobileFilters(state.filters);
  }, [JSON.stringify(state.filters)]);

  useEffect(() => {
    if (!state.loading) {
      setDrawerOpen(false);
      setIsDirty(false);
    }
  }, [state.loading]);

  const reset = () => {
    dispatch({type: "RESET"});
    dispatch({type: "TRIGGER"});
    setMobileFilters({});
    setDrawerOpen(false);
  };

  const setFilters = () => {
    if (!state.loading) {
      dispatch({
        type: "SET_LIMIT",
        payload: ProductList.PRODUCT_LIST_DEFAULT_LIMIT,
      });
      dispatch({
        type: "SET_FILTERS",
        payload: {filters: mobileFilters, doUrlParamUpdate: true},
      });
      if (isDrawerOpen) {
        setDrawerOpen(false);
      }
    }
  };

  const onRangeSelect = (filterItem: IProductListFilterItem) => (value: ProductListFilterItemRangeValue) => {
    let newMobileFilters = Object.assign({}, mobileFilters);
    const limit = state.rangeFilterMap?.get(filterItem.field);
    if (value.max === limit?.max && value.min === limit?.min) {
      delete newMobileFilters[filterItem.field];
    } else {
      newMobileFilters = {...mobileFilters, [filterItem.field]: value};
    }
    setMobileFilters(newMobileFilters);
    setIsDirty(true);
  };

  const onValueSelect = (filterItem: IProductListFilterItem) => (value: ProductListFilterItemValueType) => {
    let newMobileFilters = Object.assign({}, mobileFilters);
    if (!value || (Array.isArray(value) && !value.length)) {
      delete newMobileFilters[filterItem.field];
    } else {
      newMobileFilters = {...mobileFilters, [filterItem.field]: value};
    }
    setMobileFilters(newMobileFilters);
    setIsDirty(true);
  };

  // pre-call of the filterItemsArray() function, just to get the length of the result
  const [filterItems, booleanFilterItems] = useMemo(
    () => filterItemArray({state, onValueSelect, onRangeSelect}),
    [state.filters, state.items],
  );

  const toggleFilterView = () => {
    setDrawerOpen(!isDrawerOpen);
    toggleFilter();
  };

  return (
    <>
      {isSearchPage && <SearchHeadline />}
      <div className="column-8 filter-list--sticky">
        <div ref={filterHeadRef} className="filter-list__sort-section">
          <div>{t("product.items--quantity", {quantity: state.total})}</div>
          <button className="filter-elements__toggle-btn bold" onClick={toggleFilterView}>
            <i className="icon icon-filter icon-size--22 mr-3" />
            {isSearchPage ? (
              <span>{t("product.useFilter") + (filterNumbers > 0 ? ` (${filterNumbers})` : "")}</span>
            ) : (
              <span>{t("product.sortAndFilter") + (filterNumbers > 0 ? ` (${filterNumbers})` : "")}</span>
            )}
          </button>
        </div>
      </div>
      <div className="column-8">
        <Drawer open={isDrawerOpen} onClose={toggleFilterView}>
          <div className="filter-list p-5">
            {!isSearchPage && (
              <>
                <div className="filter-list__title bold mb-3">{t("product.sortBy")}</div>
                <ProductSortInput />
              </>
            )}
            <div className="filter-list__title bold mb-3">{t("product.useFilter")}</div>

            <div className="filter-elements custom-scrollbar">
              <div className="filter-elements__container">
                <div className="bt-default" />
                {[...filterItems, ...booleanFilterItems]}
              </div>
              <div className="filter-elements__buttons">
                <button className="cta-button cta-green my-3" onClick={setFilters} disabled={!isDirty}>
                  {state.loading ? <LoadingSpinner /> : <span>{t("product.applyFilter")}</span>}
                </button>
                <button className="cta-button cta-inverse my-3" onClick={reset}>
                  {t("product.resetAndClose")}
                </button>
              </div>
            </div>
          </div>
        </Drawer>
        <SelectedFilterOverview />
      </div>
      <div ref={productListRef} className="column-8 product-list__wrapper">
        <ListItems />
      </div>
    </>
  );
};

export default forwardRef<RefType, ProductListFilterMobileType>(ProductListFilterMobile);
